<script setup lang="ts">
import { computed } from 'vue';
import FolderIcon from '@/assets/images/svg/folder.svg?component';
import FalsePositiveIcon from '@/assets/images/svg/false-positive.svg?component';
import type { TFolder } from '@/_core/entities/report/models/types';

const props = defineProps<{
  folder: TFolder;
  isActive: boolean;
  disabled?: boolean;
}>();

defineEmits<{
  (e: 'click'): void;
}>();

const vulnCounts = computed(() => ({
  high: props.folder.statistics.find((s) => s.severity === 'high')?.count ?? 0,
  medium: props.folder.statistics.find((s) => s.severity === 'medium')?.count ?? 0,
  low: props.folder.statistics.find((s) => s.severity === 'low')?.count ?? 0,
}));

const hasAnyVulns = computed(() => props.folder.statistics.some((s) => s.count > 0));

const isFalsePositive = computed(() => props.folder.name === 'False Positives');

const icon = computed(() => (isFalsePositive.value ? FalsePositiveIcon : FolderIcon));
</script>

<template>
  <button class="tw:w-full" :disabled="disabled" @click="$emit('click')">
    <div
      class="tw:py-2.5 tw:px-3.5 tw:flex tw:items-center tw:flex-col tw:bg-white tw:text-ghost tw:typography-sidenotes tw:active:text-secondary tw:transition-colors tw:rounded-lg tw:lg:hover:bg-ghost/10 tw:outline"
      :class="[isActive ? 'tw:text-secondary tw:outline-main-blue/30' : 'tw:outline-transparent']"
    >
      <div class="tw:w-7">
        <component :is="icon" />
      </div>
      <div class="tw:flex tw:gap-0.75 tw:justify-center tw:pt-1">
        <span
          v-if="vulnCounts.high > 0"
          class="tw:shrink-0 tw:size-1 tw:rounded-full tw:bg-vuln-high"
        ></span>
        <span
          v-if="vulnCounts.medium > 0"
          class="tw:shrink-0 tw:size-1 tw:rounded-full tw:bg-vuln-medium"
        ></span>
        <span
          v-if="vulnCounts.low > 0"
          class="tw:shrink-0 tw:size-1 tw:rounded-full tw:bg-vuln-low"
        ></span>

        <span
          v-if="!hasAnyVulns"
          class="tw:shrink-0 tw:leading-0 tw:text-ghost tw:typography-sidenotes"
          >-</span
        >
      </div>
    </div>
  </button>
</template>
